import {
  type ChangeEventHandler,
  type DragEventHandler,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import type { DropzoneOptions } from '@/components/UploadTarget/types';
import { imageToFile } from '@/features/magic/utils/image.lib';
import { useFileSizeGuard } from '@/hooks/useFileSizeGuard';
import type {
  EntriesJobFile,
  QuickEntriesJobFile,
} from '@/types/gql.generated';
import { Button, useToast } from '@/ui';
import { Box, Flex, type FlexProps, Input, Text, forwardRef } from '@/ui';
import exampleImg from './assets/example-design.png';
import { allowedMimeTypes } from './constants';
import { FileDropzone } from './Dropzone';
import { UploadPreview } from './UploadPreview';

type Props = FlexProps & {
  file?: File | QuickEntriesJobFile | EntriesJobFile | null;
  instructions: string;
  isDisabled?: boolean;
  isExampleEnabled?: boolean;
  dragOverProps?: FlexProps;
  cta?: (props: DropzoneOptions) => JSX.Element;
  onSelectFile: (file: File | null) => void;
  onInstructionsChange: (instructions: string) => void;
  onDropText: (text: string) => void;
  handleClearClick: () => void;
};

export const UploadTarget = forwardRef(
  (
    {
      file,
      instructions,
      isDisabled,
      isExampleEnabled = false,
      cta,
      dragOverProps,
      onSelectFile,
      onInstructionsChange,
      onDropText,
      handleClearClick,
      ...props
    }: Props,
    ref
  ) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const toast = useToast();
    const guardFileSize = useFileSizeGuard();
    const inputId = useId();
    const inputRef = useRef<HTMLInputElement>(null);

    // Every entry point routes through here so the size and type guards can't be
    // bypassed. The file-picker input's `accept` attribute only filters the OS
    // dialog — it's not enforced by the browser and is trivially sidestepped
    // (choosing "All Files"), so a bad file type reached here unchecked and only
    // failed later, server-side, at CreateExperience (WEB-CLIENT-2AX). The drop
    // handler below already checks this explicitly; picking up the same check
    // here covers the input path too.
    const handleSelectFile = (file: File | null) => {
      if (file && !allowedMimeTypes.includes(file.type)) {
        toast.info('Image or PDF files only please!');
        return;
      }
      if (file && !guardFileSize(file)) {
        return;
      }
      onSelectFile(file);
    };

    useEffect(() => {
      if (isExampleEnabled) {
        const img = new Image();
        img.src = exampleImg;
      }
    }, [isExampleEnabled]);

    const handleDragOver: DragEventHandler = (event) => {
      event.preventDefault();

      if (!isDisabled) {
        setIsDragOver(true);
      }
    };

    const handleDrop: DragEventHandler = (event) => {
      event.preventDefault();

      setIsDragOver(false);

      if (isDisabled) {
        return;
      }

      const item = event.dataTransfer.items[0];

      if (!item) {
        return;
      }

      // user most likely dragged a png from another browser tab
      if (item.kind === 'string' && item.type === 'text/uri-list') {
        toast.info({
          title: "We don't support dragging files this way :(",
          description:
            'Please download the file to your computer first and then upload it here.',
        });
      } else if (item.kind === 'string' && item.type === 'text/plain') {
        item.getAsString(onDropText);
      } else if (item.kind === 'file') {
        const file = item.getAsFile();

        if (!file) {
          return;
        } else if (!allowedMimeTypes.includes(file.type)) {
          toast.info('Image or PDF files only please!');
        } else {
          handleSelectFile(file);
        }
      }
    };

    const handleInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      const file = event.target.files?.[0];
      if (file) {
        handleSelectFile(file);
      }
    };

    const handleExampleClick = async () => {
      const file = await imageToFile(exampleImg, 'image');
      if (!file) {
        toast.error('Failed to process the example image. Please try again.');
        return;
      }
      handleSelectFile(file);
    };

    const filename = file instanceof File ? file.name : file?.filename;
    const hasFileName = filename && filename.length > 0;

    return (
      <Flex
        align="center"
        bg="gray.100"
        borderColor="gray.300"
        borderRadius="md"
        borderStyle="dashed"
        borderWidth="3px"
        flex="1"
        flexDir="column"
        justify="center"
        minH={!file || isDragOver ? '160px' : 'auto'}
        ref={ref}
        transition="all 0.1s ease-in-out"
        w="100%"
        onDragLeave={() => setIsDragOver(false)}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        {...props}
        {...(isDragOver && {
          bg: 'gray.200',
          borderColor: 'gray.400',
          ...dragOverProps,
        })}
      >
        <Input
          accept={allowedMimeTypes.join(',')}
          display="none"
          id={inputId}
          key={filename} // ensures the same file can be selected again after remove it
          ref={inputRef}
          type="file"
          onChange={handleInputChange}
        />
        {file && !isDragOver ? (
          <UploadPreview
            file={file}
            inputId={inputId}
            instructions={instructions}
            isDisabled={isDisabled}
            onFileChange={handleSelectFile}
            onInstructionsChange={onInstructionsChange}
          />
        ) : (
          <>
            {isDragOver ? (
              <Text fontWeight="medium">Drop file to upload</Text>
            ) : (
              <Flex
                align="center"
                h="100%"
                justify="center"
                textAlign="center"
                w="100%"
                {...(!cta && {
                  as: 'label',
                  cursor: 'pointer',
                  htmlFor: isDisabled ? undefined : inputId,
                })}
              >
                <Box>
                  {typeof cta === 'function' ? (
                    cta({
                      isDisabled: Boolean(isDisabled),
                      onClick: () => inputRef.current?.click(),
                      onSelect: handleSelectFile,
                    })
                  ) : (
                    <FileDropzone isDisabled={isDisabled} />
                  )}
                </Box>
              </Flex>
            )}
          </>
        )}
        {isExampleEnabled && (
          <Button
            alignSelf="flex-end"
            colorScheme="dark"
            data-testid={hasFileName ? 'clear-control' : 'example-control'}
            mb="2"
            mr="4"
            textDecor="underline"
            variant="link"
            {...(hasFileName
              ? {
                  onClick: handleClearClick,
                  children: <span>Clear</span>,
                }
              : {
                  onClick: handleExampleClick,
                  children: <span>Try example</span>,
                })}
          />
        )}
      </Flex>
    );
  }
);
